import FVector from '@data/geometry/FVector';
import { GeometryUtils } from '@data/geometry/GeometryUtils';
import { computeOutwardOffsetPointsForEdge } from '@rendering/DesignSchemes/DesignSchemeUtils';
import * as THREE from 'three';
import { Component, FacadeComponentContext } from './Component';

// =====================================
// TYPES
// =====================================

export type BalconyConfig = {
    depth: number;
    connectCorners: boolean;
};

export class BalconyComponent extends Component {
    // =====================================
    // CLASS FIELDS
    // =====================================

    private readonly depth: number;
    private readonly thickness: number;
    private readonly perimeter: FVector[];
    private readonly edgeIndex: number;
    private readonly renderedIndices: Set<number>;
    private readonly connectCorners: boolean;
    private readonly requireActiveSensors: boolean;

    constructor(context: FacadeComponentContext, config: BalconyConfig) {
        super(context);
        this.depth = THREE.MathUtils.clamp(config.depth, 0.1, 10);
        this.thickness = context.thickness;
        this.perimeter = context.perimeter;
        this.edgeIndex = context.edgeIndex;
        this.renderedIndices = context.renderedIndices;
        this.connectCorners = config.connectCorners;
        this.requireActiveSensors = context.requireActiveSensors;
    }

    // =====================================
    // HELPER FUNCTIONS
    // =====================================

    private findVertexIndex(poly: THREE.Vector2[], v: THREE.Vector2, eps = 1e-8): number {
        for (let i = 0; i < poly.length; i++) {
            if (poly[i].distanceToSquared(v) < eps * eps) return i;
        }
        return -1;
    }

    // Match PV behavior: corners auto-connect when obtuse.
    private isObtuseCorner(vertexIndex: number): boolean {
        const count = this.perimeter.length;
        if (count < 3 || vertexIndex < 0 || vertexIndex >= count) return false;

        const prev = this.perimeter[(vertexIndex - 1 + count) % count];
        const vertex = this.perimeter[vertexIndex];
        const next = this.perimeter[(vertexIndex + 1) % count];

        const incoming = new THREE.Vector2(vertex.x - prev.x, vertex.y - prev.y);
        const outgoing = new THREE.Vector2(next.x - vertex.x, next.y - vertex.y);
        const incomingLength = incoming.length();
        const outgoingLength = outgoing.length();
        if (incomingLength <= 1e-9 || outgoingLength <= 1e-9) return false;

        const incomingNorm = incoming.clone().divideScalar(incomingLength);
        const outgoingNorm = outgoing.clone().divideScalar(outgoingLength);
        const unsignedAngle = incomingNorm.angleTo(outgoingNorm);
        const crossZ = incomingNorm.x * outgoingNorm.y - incomingNorm.y * outgoingNorm.x;
        const isCCW = GeometryUtils.polygonIsCCW(this.perimeter.map((p) => new THREE.Vector2(p.x, p.y)));
        const isReflex = isCCW ? crossZ < 0 : crossZ > 0;
        const interiorAngle = isReflex ? Math.PI * 2 - unsignedAngle : unsignedAngle;

        return interiorAngle > Math.PI / 2;
    }

    private computeOffsetPointsForEdge(a: FVector, b: FVector): [THREE.Vector2, THREE.Vector2] {
        return computeOutwardOffsetPointsForEdge(this.perimeter, a, b, this.depth);
    }

    private addQuad(group: THREE.Group, points: FVector[]): void {
        const geom = new THREE.BufferGeometry().setFromPoints(points.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
        geom.setIndex([0, 1, 2, 0, 2, 3]);
        geom.computeVertexNormals();
        const mesh = new THREE.Mesh(geom, this.material);
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geom, 0.01), BalconyComponent.edgeMaterial);
        group.add(mesh, edges);
    }

    // =====================================
    // MAIN GENERATION FUNCTION
    // =====================================

    override build(): THREE.Group {
        const group = new THREE.Group();
        if (!this.renderedIndices.has(this.edgeIndex)) return group;

        // If required, only render if at least one sensor is active in the facade:
        if (this.requireActiveSensors && !this.sensors.some((sensor) => sensor.getScalarValue() > 0)) return group;

        const poly2 = this.perimeter.map((p) => new THREE.Vector2(p.x, p.y));
        const idx = this.findVertexIndex(poly2, new THREE.Vector2(this.p1.x, this.p1.y));
        const idx2 = this.findVertexIndex(poly2, new THREE.Vector2(this.p2.x, this.p2.y));
        if (idx < 0 || idx2 < 0) return group;

        let [p1Out, p2Out] = this.computeOffsetPointsForEdge(this.p1, this.p2);
        const connectPrevCorner = this.connectCorners || this.isObtuseCorner(idx);
        const connectNextCorner = this.connectCorners || this.isObtuseCorner(idx2);

        const prevEdgeIndex = (this.edgeIndex - 1 + this.perimeter.length) % this.perimeter.length;
        const nextEdgeIndex = (this.edgeIndex + 1) % this.perimeter.length;
        const prevConnectable = connectPrevCorner && this.renderedIndices.has(prevEdgeIndex);
        const nextConnectable = connectNextCorner && this.renderedIndices.has(nextEdgeIndex);

        const currLineA = p1Out.clone();
        const currLineB = p2Out.clone();

        if (prevConnectable) {
            const prevA = this.perimeter[(idx - 1 + this.perimeter.length) % this.perimeter.length];
            const prevB = this.perimeter[idx];
            const [prevOutA, prevOutB] = this.computeOffsetPointsForEdge(prevA, prevB);
            const interPrev = GeometryUtils.intersect2D(prevOutA, prevOutB, currLineA, currLineB);
            if (interPrev) p1Out.set(interPrev.x, interPrev.y);
        }

        if (nextConnectable) {
            const nextA = this.perimeter[idx2];
            const nextB = this.perimeter[(idx2 + 1) % this.perimeter.length];
            const [nextOutA, nextOutB] = this.computeOffsetPointsForEdge(nextA, nextB);
            const interNext = GeometryUtils.intersect2D(currLineA, currLineB, nextOutA, nextOutB);
            if (interNext) p2Out.set(interNext.x, interNext.y);
        }

        const zTop = this.p1.z;
        const zBottom = zTop - this.thickness;

        const p1Top = new FVector(this.p1.x, this.p1.y, zTop);
        const p2Top = new FVector(this.p2.x, this.p2.y, zTop);
        const p1OutTop = new FVector(p1Out.x, p1Out.y, zTop);
        const p2OutTop = new FVector(p2Out.x, p2Out.y, zTop);

        const p1Bottom = new FVector(this.p1.x, this.p1.y, zBottom);
        const p2Bottom = new FVector(this.p2.x, this.p2.y, zBottom);
        const p1OutBottom = new FVector(p1Out.x, p1Out.y, zBottom);
        const p2OutBottom = new FVector(p2Out.x, p2Out.y, zBottom);

        // Top slab face:
        this.addQuad(group, [p1Top, p2Top, p2OutTop, p1OutTop]);
        // Bottom slab face:
        this.addQuad(group, [p1OutBottom, p2OutBottom, p2Bottom, p1Bottom]);
        // Outer vertical fascia:
        this.addQuad(group, [p1OutTop, p2OutTop, p2OutBottom, p1OutBottom]);

        // Side caps only where this edge does not connect to neighbour balconies.
        if (!prevConnectable) {
            this.addQuad(group, [p1Top, p1OutTop, p1OutBottom, p1Bottom]);
        }
        if (!nextConnectable) {
            this.addQuad(group, [p2OutTop, p2Top, p2Bottom, p2OutBottom]);
        }

        return group;
    }
}
